/**
 * The Desktop's context menu — right-click, Shift+F10 / the Menu key, or a
 * long press on touch. A plain `role="menu"` at the pointer, kept inside the
 * viewport; arrow keys move, Enter activates, Escape or a click outside
 * closes, and focus returns where it came from.
 *
 * An entry with `items` opens a submenu (Windows' "View ▸", "Sort by ▸"):
 * on hover, ArrowRight or Enter; ArrowLeft or Escape closes it again.
 *
 * What the menu OFFERS is built by the Desktop from the shared file actions,
 * so it never offers more than Browse does for the same file.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MenuEntry {
  label: string;
  onSelect?: () => void;
  danger?: boolean;
  /** Draw a divider above this entry. */
  separatorBefore?: boolean;
  shortcut?: string;
  /** A radio-style mark (the current view, the current sort). */
  checked?: boolean;
  /** A submenu instead of an action. */
  items?: MenuEntry[];
}

export function DesktopContextMenu({
  x,
  y,
  entries,
  label,
  onClose,
  returnFocus,
  style = 'windows',
}: {
  x: number;
  y: number;
  entries: readonly MenuEntry[];
  label: string;
  onClose: () => void;
  returnFocus?: HTMLElement | null;
  style?: 'windows' | 'mac';
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onBlur = () => onClose();
    window.addEventListener('pointerdown', onPointer, true);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('pointerdown', onPointer, true);
      window.removeEventListener('blur', onBlur);
    };
  }, [onClose]);

  const close = (restore: boolean) => {
    onClose();
    if (restore) returnFocus?.focus?.({ preventScroll: true });
  };

  return (
    <div ref={ref} onContextMenu={(e) => e.preventDefault()}>
      <MenuPanel
        x={x}
        y={y}
        entries={entries}
        label={label}
        style={style}
        autoFocus
        onActivate={() => close(false)}
        onEscape={() => close(true)}
      />
    </div>
  );
}

function MenuPanel({
  x,
  y,
  entries,
  label,
  style,
  autoFocus,
  onActivate,
  onEscape,
  onBack,
}: {
  x: number;
  y: number;
  entries: readonly MenuEntry[];
  label: string;
  style: 'windows' | 'mac';
  autoFocus: boolean;
  onActivate: () => void;
  onEscape: () => void;
  /** A submenu: ArrowLeft returns to the parent. */
  onBack?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [open, setOpen] = useState<{ index: number; x: number; y: number; focus: boolean } | null>(
    null,
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const left = Math.max(8, Math.min(x, window.innerWidth - rect.width - 8));
    const top = Math.max(8, Math.min(y, window.innerHeight - rect.height - 8));
    setPos({ left, top });
    if (autoFocus) el.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
  }, [x, y, autoFocus]);

  const openSub = (index: number, button: HTMLElement, focus: boolean) => {
    const rect = button.getBoundingClientRect();
    setOpen({ index, x: rect.right - 2, y: rect.top - 4, focus });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Keys from a submenu bubble here too; only handle this panel's own.
    if ((e.target as HTMLElement).closest('[role="menu"]') !== ref.current) return;
    const buttons = Array.from(
      ref.current?.querySelectorAll<HTMLButtonElement>(':scope > div > [role="menuitem"]') ?? [],
    );
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      buttons[(index + step + buttons.length) % buttons.length]?.focus();
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      buttons[e.key === 'Home' ? 0 : buttons.length - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index >= 0 && entries[index]?.items) {
      e.preventDefault();
      openSub(index, buttons[index], true);
    } else if (e.key === 'ArrowLeft' && onBack) {
      e.preventDefault();
      e.stopPropagation();
      onBack();
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      if (onBack && e.key === 'Escape') onBack();
      else onEscape();
    }
  };

  const mac = style === 'mac';
  return (
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      onKeyDown={onKeyDown}
      data-submenu={onBack ? 'true' : undefined}
      className={cn(
        'fixed z-[60] min-w-[12rem] border p-1 text-sm text-popover-foreground',
        mac
          ? 'rounded-lg border-black/10 bg-popover/95 shadow-xl backdrop-blur-md dark:border-white/10'
          : 'rounded-md border-border bg-popover shadow-lg',
      )}
      style={{ left: pos.left, top: pos.top }}
    >
      {entries.map((entry, i) => (
        <div key={`${entry.label}-${i}`}>
          {entry.separatorBefore && i > 0 && <div className="my-1 border-t border-border" />}
          <button
            type="button"
            role="menuitem"
            aria-haspopup={entry.items ? 'menu' : undefined}
            aria-expanded={entry.items ? open?.index === i : undefined}
            onPointerEnter={(e) => {
              if (entry.items) openSub(i, e.currentTarget, false);
              else if (open) setOpen(null);
            }}
            onClick={(e) => {
              if (entry.items) {
                openSub(i, e.currentTarget, true);
                return;
              }
              onActivate();
              entry.onSelect?.();
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left outline-none',
              mac
                ? 'hover:bg-[#0a64d6] hover:text-white focus-visible:bg-[#0a64d6] focus-visible:text-white'
                : 'hover:bg-muted focus-visible:bg-muted',
              entry.danger && !mac && 'text-destructive',
            )}
          >
            <Check
              className={cn('h-3.5 w-3.5 shrink-0', entry.checked ? 'opacity-100' : 'opacity-0')}
              aria-hidden
            />
            <span className="flex-1">{entry.label}</span>
            {entry.shortcut && <span className="text-xs opacity-60">{entry.shortcut}</span>}
            {entry.items && <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />}
          </button>
        </div>
      ))}
      {open && entries[open.index]?.items && (
        <MenuPanel
          key={open.index}
          x={open.x}
          y={open.y}
          entries={entries[open.index].items!}
          label={entries[open.index].label}
          style={style}
          autoFocus={open.focus}
          onActivate={onActivate}
          onEscape={onEscape}
          onBack={() => {
            const index = open.index;
            setOpen(null);
            const items = ref.current?.querySelectorAll<HTMLButtonElement>(
              ':scope > div > [role="menuitem"]',
            );
            items?.[index]?.focus();
          }}
        />
      )}
    </div>
  );
}
